<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { api } from '../api'
import { useI18n } from '../i18n'
import { session } from '../session'

const router = useRouter()
const { locale, t } = useI18n()
const stations = ref([])
const activeStation = ref(null)
const loading = ref(true)
const catalogLoading = ref(false)
const templates = ref([])
const favoriteIds = ref([])
const pendingFavoriteId = ref('')
const searchQuery = ref('')
const currentPage = ref(1)
const error = ref('')
const pageSize = 8

function matchesSearch(template) {
  const search = searchQuery.value.trim().toLocaleLowerCase()
  return !search || `${template.displayName} ${template.kind}`.toLocaleLowerCase().includes(search)
}

function compareTemplates(left, right) {
  return left.displayName.localeCompare(right.displayName, locale.value)
}

const favoriteTemplates = computed(() => {
  const templatesById = new Map(templates.value.map((template) => [template.id, template]))
  return favoriteIds.value
    .map((templateId) => templatesById.get(templateId))
    .filter((template) => template && matchesSearch(template))
})

const catalogTemplates = computed(() => templates.value
  .filter((template) => !favoriteIds.value.includes(template.id) && matchesSearch(template))
  .sort(compareTemplates))

const totalPages = computed(() => Math.max(1, Math.ceil(catalogTemplates.value.length / pageSize)))
const pagedTemplates = computed(() => {
  const start = (currentPage.value - 1) * pageSize
  return catalogTemplates.value.slice(start, start + pageSize)
})

const visibleTemplateCount = computed(() => favoriteTemplates.value.length + catalogTemplates.value.length)

function isFavorite(templateId) {
  return favoriteIds.value.includes(templateId)
}

function isActiveStation(station) {
  return activeStation.value?.id === station.id
}

async function loadCatalog() {
  catalogLoading.value = true
  try {
    const catalog = await api('/catalog')
    templates.value = catalog.items
    try {
      const favorites = await api('/favorites')
      favoriteIds.value = favorites.templateIds
    } catch (cause) {
      favoriteIds.value = []
      error.value = t('favoritesUnavailable')
    }
  } catch (cause) {
    error.value = cause.message
  } finally {
    catalogLoading.value = false
  }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await api('/stations')
    stations.value = result.stations
    activeStation.value = result.activeStation
    await loadCatalog()
  } catch (cause) {
    error.value = cause.message
  } finally {
    loading.value = false
  }
}

async function selectStation(profileId) {
  if (activeStation.value?.id === profileId) return
  error.value = ''
  try {
    const result = await api('/stations/active', { method: 'PUT', body: JSON.stringify({ profileId }) })
    activeStation.value = result.activeStation
  } catch (cause) {
    error.value = cause.message
  }
}

async function toggleFavorite(template) {
  error.value = ''
  pendingFavoriteId.value = template.id
  try {
    if (isFavorite(template.id)) {
      await api(`/favorites/${template.id}`, { method: 'DELETE' })
      favoriteIds.value = favoriteIds.value.filter((id) => id !== template.id)
    } else {
      await api(`/favorites/${template.id}`, { method: 'POST' })
      favoriteIds.value = [...favoriteIds.value, template.id]
    }
  } catch (cause) {
    error.value = cause.message
  } finally {
    pendingFavoriteId.value = ''
  }
}

function openTemplate(template) {
  router.push({ name: 'report-detail', params: { templateId: template.id } })
}

function changePage(page) {
  currentPage.value = Math.min(Math.max(page, 1), totalPages.value)
}

function manageStations() {
  router.push({ name: 'admin-stations' })
}

onMounted(load)

watch([searchQuery, favoriteIds, catalogTemplates], () => {
  currentPage.value = 1
})

watch(totalPages, (pageCount) => {
  if (currentPage.value > pageCount) currentPage.value = pageCount
})

watch(() => session.user?.reportLanguage, (language, previousLanguage) => {
  if (language && previousLanguage && language !== previousLanguage) loadCatalog()
})
</script>

<template>
  <section class="reports-workspace">
    <div class="workspace-heading">
      <h1>{{ t('reports') }}</h1>
    </div>
    <p v-if="error" class="notice error" role="alert">{{ error }}</p>

    <div v-if="loading" class="empty">{{ t('stationsLoading') }}</div>
    <div v-else class="reports-workspace-grid">
      <aside class="station-panel card" :aria-label="t('stations')">
        <div class="station-panel-heading">
          <h2>{{ t('stations') }}</h2>
        </div>

        <div v-if="stations.length" class="station-list">
          <button v-for="station in stations" :key="station.id" class="station-choice" type="button"
            :class="{ selected: isActiveStation(station) }" :aria-pressed="isActiveStation(station)"
            :disabled="isActiveStation(station)" @click="selectStation(station.id)">
            <span class="station-choice-copy">
              <strong>{{ station.displayName }}</strong>
              <small>{{ station.host }}:{{ station.port }} · {{ station.databaseName }}</small>
            </span>
            <span v-if="isActiveStation(station)" class="station-current-mark" aria-hidden="true">{{ t('currentStation') }}</span>
            <span v-else class="station-select-mark">{{ t('chooseStation') }}</span>
          </button>
        </div>
        <p v-else class="station-panel-empty">{{ t('noStations') }}</p>
        <div v-if="session.user?.role === 'ADMIN'" class="station-panel-footer">
          <button class="button button-secondary" type="button" @click="manageStations">{{ t('manageStations') }}</button>
        </div>
      </aside>

      <section class="catalog-panel card" :aria-label="t('catalogTitle')">
        <div class="catalog-panel-heading">
          <div>
            <h2>{{ t('catalogTitle') }}</h2>
          </div>
          <span class="muted">{{ searchQuery ? t('filteredAvailable', { visible: visibleTemplateCount, total: templates.length }) : t('available', { count: templates.length }) }}</span>
        </div>

        <label class="catalog-search">
          <span class="visually-hidden">{{ t('searchReports') }}</span>
          <input v-model="searchQuery" type="search" autocomplete="off" :placeholder="t('searchPlaceholder')">
        </label>

        <p v-if="catalogLoading" class="empty">{{ t('catalogLoading') }}</p>
        <template v-else>
          <section v-if="favoriteTemplates.length" class="report-group favorites-group" :aria-label="t('favoritesTitle')">
            <div class="report-group-heading">
              <h3>{{ t('favoritesTitle') }} <span>{{ favoriteTemplates.length }}</span></h3>
            </div>
            <div class="report-list">
              <article v-for="template in favoriteTemplates" :key="template.id" class="report-row">
                <button class="report-row-open" type="button" @click="openTemplate(template)">
                  <span class="template-kind" :class="{ chart: template.kind === 'CHART' }">{{ template.kind === 'CHART' ? t('chart') : t('report') }}</span>
                  <strong>{{ template.displayName }}</strong>
                  <small>{{ t('parameters', { count: template.parameterCount }) }}</small>
                </button>
                <button class="favorite-button" type="button" :class="{ active: isFavorite(template.id) }" :disabled="pendingFavoriteId === template.id"
                  :aria-label="t('removeFavorite')" @click="toggleFavorite(template)">★</button>
              </article>
            </div>
          </section>

          <section class="report-group" :aria-label="t('allReports')">
            <div class="report-group-heading">
              <h3>{{ t('allReports') }}</h3>
              <span v-if="totalPages > 1" class="muted">{{ t('pageOf', { current: currentPage, total: totalPages }) }}</span>
            </div>
            <div v-if="pagedTemplates.length" class="report-list">
              <article v-for="template in pagedTemplates" :key="template.id" class="report-row">
                <button class="report-row-open" type="button" @click="openTemplate(template)">
                  <span class="template-kind" :class="{ chart: template.kind === 'CHART' }">{{ template.kind === 'CHART' ? t('chart') : t('report') }}</span>
                  <strong>{{ template.displayName }}</strong>
                  <small>{{ t('parameters', { count: template.parameterCount }) }}</small>
                </button>
                <button class="favorite-button" type="button" :class="{ active: isFavorite(template.id) }" :disabled="pendingFavoriteId === template.id"
                  :aria-label="isFavorite(template.id) ? t('removeFavorite') : t('addFavorite')" @click="toggleFavorite(template)">{{ isFavorite(template.id) ? '★' : '☆' }}</button>
              </article>
            </div>
            <p v-else class="empty">{{ t('noSearchResults') }}</p>
          </section>

          <nav v-if="totalPages > 1" class="catalog-pagination" :aria-label="t('pagination')">
            <button class="button button-secondary button-small" type="button" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">{{ t('previousPage') }}</button>
            <button v-for="page in totalPages" :key="page" class="catalog-page" type="button" :class="{ active: page === currentPage }"
              :aria-label="t('pageNumber', { page })" :aria-current="page === currentPage ? 'page' : undefined" @click="changePage(page)">{{ page }}</button>
            <button class="button button-secondary button-small" type="button" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">{{ t('nextPage') }}</button>
          </nav>
        </template>
      </section>
    </div>
  </section>
</template>
